/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUsuarioWithRole } from '../../hooks/useUsuarioWithRole';
import {
  Alert,
  Snackbar,
  Button,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Modal,
  Box,
  Tooltip,
  Container,
  Chip,
  Divider,
  Skeleton,
  IconButton,
  InputAdornment,
  Fade,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Share as ShareIcon,
  PlayArrow as PlayArrowIcon,
  AccessTime as AccessTimeIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'animate.css';
import 'react-quill/dist/quill.snow.css';
import './Cview.css';
import coursesService from '../../services/CoursesService';
import analyticsService from '../../services/AnalyticsService';
import teacherService from '../../services/TeacherService';
import { tokenUtils } from '../../utils/tokenUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWelcomeCourseStatus } from '../../hooks/useWelcomeCourseStatus';
import WelcomeCourseBanner from '../common/WelcomeCourseBanner';

// Función para limpiar HTML y extraer texto plano
const DESCRIPTION_TRUNCATE_LIMIT = 120;
const COMPACT_COLLAPSED_MIN_HEIGHT = 420;

const matchesCourseId = (course, targetId) => {
  if (!course || course.id == null || targetId == null || targetId === '') return false;
  return String(course.id) === String(targetId);
};

const safeDecodeURIComponent = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const stripHtmlTags = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const normalizeSharedTitle = (value) =>
  stripHtmlTags(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

const findPublicCourseBySharedInfo = (courses, sharedInfo) => {
  const sharedTitle = normalizeSharedTitle(sharedInfo?.title);
  if (!sharedTitle) return null;

  return (courses || []).find((course) => {
    const courseTitle = normalizeSharedTitle(course?.title);
    if (!courseTitle) return false;
    return (
      courseTitle === sharedTitle ||
      courseTitle.includes(sharedTitle) ||
      sharedTitle.includes(courseTitle)
    );
  });
};

const buildSharedCoursePreview = (sharedInfo) => ({
  id: Number(sharedInfo.id) || sharedInfo.id,
  title: sharedInfo.title || 'Curso compartido',
  description: 'Explora este curso e inscríbete para acceder al contenido completo.',
  teacherName: '',
});

/** Ruta de miniatura según lo que devuelva el API (público vs autenticado). */
const getCourseThumbnailSrc = (course) => {
  if (!course) return null;
  const path =
    course.thumbnailUrl ||
    course.thumbnailPath ||
    course.thumbnail ||
    course.Thumbnail;
  if (!path) return null;
  if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) {
    return path;
  }
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
};

/** ID del instructor según distintas formas que devuelve el API */
const getCourseTeacherId = (course) => {
  if (!course) return null;
  const id =
    course.teacherId ??
    course.teacher_id ??
    course.TeacherId ??
    course.Teacher_Id ??
    course.teached_id ??
    course.TeachedId;
  if (id === undefined || id === null || id === '') return null;
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
};

const getTeacherDisplayName = (course, teacherNames) => {
  const id = getCourseTeacherId(course);
  const fromMap = id != null ? (teacherNames[id] ?? teacherNames[String(id)]) : undefined;
  const embedded =
    course?.teacherName ||
    course?.TeacherName ||
    course?.teacher?.nombre ||
    course?.Teacher?.Nombre;
  if (embedded) return embedded;
  if (fromMap) return fromMap;
  if (id != null) return `Instructor #${id}`;
  return 'Instructor no disponible';
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 1000,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24,
  p: 0,
  overflowY: 'auto',
};

const CourseCard = ({
  course,
  teacherNames,
  onOpenModal,
  onToggleDescription,
  isExpanded,
  onEnroll,
  isEnrolled,
  hasPending,
  navigate,
  compact = false,
  onLoginRequired = null,
  /** Si se define en modo compacto, el clic de la tarjeta navega en lugar de abrir el modal */
  onCompactNavigate = null,
  /** 0–100: muestra barra de progreso (p. ej. Mis cursos) */
  progressPercent = null,
  /** Botón Comenzar / Continuar / Revisar curso (pestaña Mis cursos) */
  showMyCoursesButton = false,
  /** Si true, no permite inscribirse (p. ej. gate de bienvenida) */
  enrollmentBlocked = false,
}) => {
  const [enrolling, setEnrolling] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isRecommended = Boolean(course?.isRecommended ?? course?.IsRecommended);
  const isWelcome = Boolean(course?.isWelcome ?? course?.IsWelcome);
  const plainDescription = stripHtmlTags(course.description || '') || 'Sin descripción disponible';
  const shouldTruncate = plainDescription.length > DESCRIPTION_TRUNCATE_LIMIT;
  const displayDescription = shouldTruncate && !isExpanded
    ? `${plainDescription.slice(0, DESCRIPTION_TRUNCATE_LIMIT)}...`
    : plainDescription;

  const handleToggleDescription = (e) => {
    e.stopPropagation();
    onToggleDescription(course.id);
  };

  // Verificar si hay usuario logueado
  const usuario = JSON.parse(localStorage.getItem('usuarioConCedula') || '{}');
  const isUserLoggedIn = !!usuario?.id;
  
  // Si está en modo compacto y el usuario está logueado, la tarjeta es clickeable
  const isCardClickable = compact && isUserLoggedIn;

  const handleCardClick = () => {
    if (!isCardClickable) return;
    if (onCompactNavigate) {
      onCompactNavigate(course);
      return;
    }
    onOpenModal(course);
  };

  const handleEnrollClick = async () => {
    // Verificar si hay usuario antes de intentar inscribirse
    if (!usuario?.id) {
      if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }

    if (enrollmentBlocked) return;

    setEnrolling(true);
    try {
      await onEnroll(course.id);
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <Card
      className="animate__animated animate__fadeInUp"
      onClick={handleCardClick}
      sx={{
        width: compact ? '100%' : 320,
        maxWidth: compact ? '280px' : 320,
        minHeight: compact ? (isExpanded ? COMPACT_COLLAPSED_MIN_HEIGHT : COMPACT_COLLAPSED_MIN_HEIGHT) : 480,
        height: compact ? (isExpanded ? 'auto' : '100%') : (isExpanded ? 'auto' : 480),
        alignSelf: isExpanded ? 'flex-start' : 'stretch',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: compact ? 2 : 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: compact ? '0 1px 3px rgba(0,0,0,0.12)' : 2,
        border: compact ? 'none' : '1px solid rgba(0,0,0,0.08)',
        overflow: 'hidden',
        cursor: isCardClickable ? 'pointer' : 'default',
        '&:hover': {
          transform: compact ? 'translateY(-4px)' : 'translateY(-8px)',
          boxShadow: compact ? '0 4px 12px rgba(0,0,0,0.15)' : 8,
        },
      }}
    >
      <Box 
        className="course-image-container"
        sx={{
          position: 'relative',
          width: '100%',
          // La miniatura ocupa aprox. 25% del alto de la pantalla
          // y se limita para mantener proporción visual en extremos.
          height: compact ? 180 : '25vh',
          minHeight: compact ? 180 : 220,
          maxHeight: compact ? 180 : 340,
          overflow: 'hidden',
          boxSizing: 'border-box',
          p: 1.25,
          borderRadius: compact ? '8px 8px 0 0' : '12px 12px 0 0',
          backgroundColor: '#e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {imageError || !getCourseThumbnailSrc(course) ? (
          // Placeholder cuando no hay imagen
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              bgcolor: '#f3f4f6'
            }}
          >
            <SchoolIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          </Box>
        ) : (
          <CardMedia
            component="img"
            image={getCourseThumbnailSrc(course) || '/img/default.png'}
            alt={course.title}
            className="course-image"
            onError={() => setImageError(true)}
            sx={{
              display: 'block',
              width: 'auto',
              height: 'auto',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
          />
        )}
        {/* Logo Docentia pequeño en la esquina superior izquierda - Solo en modo compacto */}
        {compact && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: '#4DABA4',
              borderRadius: 1.5,
              px: 1,
              py: 0.4
            }}
          >
            <img 
              src="/icons/Logo completo.png" 
              alt="Docentia" 
              style={{ height: '14px', objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}
        {(isWelcome || isRecommended) ? (
          <Box
            sx={{
              position: 'absolute',
              top: compact ? 6 : 12,
              right: compact ? 6 : 12,
              zIndex: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
            }}
          >
            {isWelcome && (
              <Chip
                label="Bienvenida"
                size="small"
                sx={{
                  height: compact ? 22 : 24,
                  bgcolor: '#F59E0B',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: compact ? '0.65rem' : '0.72rem',
                  letterSpacing: 0.2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
            {isRecommended && (
              <Chip
                label="Recomendado"
                size="small"
                sx={{
                  height: compact ? 22 : 24,
                  bgcolor: '#4DABA4',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: compact ? '0.65rem' : '0.72rem',
                  letterSpacing: 0.2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>
        ) : (
          !compact && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor: 'rgba(255,255,255,0.9)',
                borderRadius: 2,
                px: 1,
                py: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                <SchoolIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Curso
              </Typography>
            </Box>
          )
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: compact ? 2 : 2.5, pb: compact ? 1 : 2.5 }}>
        {/* Título del curso */}
        <Tooltip title={stripHtmlTags(course.title)} placement="top">
          <Typography
            variant={compact ? "subtitle1" : "h6"}
            sx={{ 
              fontWeight: 700,
              mb: compact ? 0.75 : 1.5,
              display: compact ? '-webkit-box' : 'block',
              WebkitLineClamp: compact ? 2 : 'unset',
              WebkitBoxOrient: compact ? 'vertical' : 'unset',
              overflow: compact ? 'hidden' : 'visible',
              minHeight: compact ? '2.7rem' : 'unset',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
              color: 'text.primary',
              fontSize: compact ? '1.1rem' : '1.25rem'
            }}
            dangerouslySetInnerHTML={{ __html: course.title }}
          />
        </Tooltip>

        {/* Nombre del instructor */}
        <Typography
          variant="body2"
          sx={{
            mb: compact ? 0.75 : 1,
            fontWeight: 400,
            color: 'text.secondary',
            fontSize: compact ? '0.85rem' : '0.9rem',
            lineHeight: 1.4
          }}
        >
          {getTeacherDisplayName(course, teacherNames)}
        </Typography>

        {/* Descripción corta - Solo en modo compacto */}
        {compact ? (
          <>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', mb: progressPercent != null && !Number.isNaN(Number(progressPercent)) ? 1 : 1.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                  display: isExpanded ? 'block' : '-webkit-box',
                  WebkitLineClamp: isExpanded ? 'unset' : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: isExpanded ? 'visible' : 'hidden',
                  minHeight: isExpanded ? 'auto' : '3.6rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {displayDescription}
              </Typography>
              {shouldTruncate && (
                <Button
                  size="small"
                  onClick={handleToggleDescription}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    fontSize: '0.75rem',
                    mt: 0.5,
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    color: '#4DABA4',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {isExpanded ? 'Ver menos' : 'Ver más'}
                </Button>
              )}
            </Box>
            {progressPercent != null && !Number.isNaN(Number(progressPercent)) && (
              <Box sx={{ mt: 0.5, mb: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Progreso
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#4DABA4', fontSize: '0.7rem' }}>
                    {Math.round(Math.min(100, Math.max(0, Number(progressPercent))))}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, Number(progressPercent)))}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(77, 171, 164, 0.15)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      bgcolor: '#4DABA4',
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                lineHeight: 1.6,
                overflow: isExpanded ? 'visible' : 'hidden',
                textAlign: 'justify'
              }}
            >
              {displayDescription}
              {shouldTruncate && (
                <Button
                  size="small"
                  onClick={handleToggleDescription}
                  sx={{ 
                    p: 0, 
                    minWidth: 'auto', 
                    fontSize: '0.75rem',
                    ml: 0.5,
                    textTransform: 'none',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {isExpanded ? 'Ver menos' : 'Ver más'}
                </Button>
              )}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Chip
                label={getTeacherDisplayName(course, teacherNames)}
                size="small"
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  height: 24
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {new Date(course.createdAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>

      <CardActions sx={{ p: compact ? 2 : 2.5, pt: compact ? 0 : 0, pb: compact ? 2 : 2.5, gap: 1, mt: compact ? 'auto' : 0 }}>
        {compact ? (
          !isUserLoggedIn ? (
            <Button
              fullWidth
              size="medium"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                if (onLoginRequired) {
                  onLoginRequired();
                }
              }}
              sx={{ 
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                py: 1.25,
                backgroundColor: '#4DABA4',
                color: 'white',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#3a8a84',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }
              }}
            >
              Ingresa
            </Button>
          ) : showMyCoursesButton && onCompactNavigate && progressPercent != null && !Number.isNaN(Number(progressPercent)) ? (
            <Button
              fullWidth
              size="medium"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                onCompactNavigate(course);
              }}
              startIcon={
                Math.round(Math.min(100, Math.max(0, Number(progressPercent)))) >= 100 ? (
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 20 }} />
                )
              }
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                py: 1.25,
                backgroundColor: '#4DABA4',
                color: 'white',
                boxShadow: '0 4px 12px rgba(77, 171, 164, 0.3)',
                '&:hover': {
                  backgroundColor: '#3a8a84',
                  boxShadow: '0 6px 16px rgba(77, 171, 164, 0.35)',
                },
              }}
            >
              {Math.round(Math.min(100, Math.max(0, Number(progressPercent)))) >= 100
                ? 'Revisar curso'
                : Math.round(Math.min(100, Math.max(0, Number(progressPercent)))) > 0
                  ? 'Continuar'
                  : 'Comenzar curso'}
            </Button>
          ) : null
        ) : (
          <>
            <Button 
              size="medium" 
              variant="outlined"
              onClick={() => onOpenModal(course)}
              startIcon={<DescriptionIcon />}
              className="border-green-aqua"
              sx={{ 
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: '#4DABA4',
                borderColor: '#4DABA4',
                '&:hover': {
                  borderColor: '#4DABA4',
                  backgroundColor: 'rgba(77, 171, 164, 0.1)'
                }
              }}
            >
              Detalles
            </Button>
            {isEnrolled ? (
              <Button
                size="medium"
                variant="contained"
                color="success"
                onClick={() => navigate(`/courses/Introcourse/${course.id}`, { state: { objeto: course.id } })}
                startIcon={<PlayArrowIcon />}
                sx={{ 
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2,
                  backgroundColor: '#10B981',
                  '&:hover': {
                    backgroundColor: '#059669',
                    boxShadow: 4
                  }
                }}
              >
                Continuar
              </Button>
            ) : hasPending ? (
              <Button
                size="medium"
                variant="contained"
                disabled
                startIcon={<AccessTimeIcon />}
                sx={{ 
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2,
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#F59E0B',
                    boxShadow: 4
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#F59E0B',
                    color: 'white'
                  }
                }}
              >
                Pendiente
              </Button>
            ) : (
              <Button
                size="medium"
                variant="contained"
                onClick={handleEnrollClick}
                disabled={enrolling || enrollmentBlocked}
                startIcon={enrolling ? <CircularProgress size={16} /> : <SchoolIcon />}
                className="background-green-aqua"
                sx={{ 
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2,
                  backgroundColor: '#4DABA4',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#4DABA4',
                    boxShadow: 4
                  },
                  '&.Mui-disabled': {
                    backgroundColor: enrollmentBlocked ? '#9ca3af' : '#4DABA4',
                    color: 'white'
                  }
                }}
              >
                {enrolling ? 'Inscribiendo...' : enrollmentBlocked ? 'Bloqueado' : 'Inscribirse'}
              </Button>
            )}
          </>
        )}
      </CardActions>
    </Card>
  );
};

// Modal de éxito para confirmación de inscripción
const SuccessModal = ({ open, courseTitle, onClose }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 24,
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4, px: 4 }}>
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: '#4DABA4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            border: '4px solid white',
            boxShadow: '0 4px 12px rgba(77, 171, 164, 0.3)',
          }}
        >
          <SentimentSatisfiedIcon 
            sx={{ 
              fontSize: 60, 
              color: 'white' 
            }} 
          />
        </Box>
        
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            mb: 2,
            color: '#2d3748',
            lineHeight: 1.5
          }}
        >
          Te has inscrito al curso <strong>{courseTitle}</strong> con éxito
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#64748b',
            mb: 3
          }}
        >
          Ya puedes acceder al contenido del curso
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 4, px: 4 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#4DABA4',
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: '#3a8a84',
            }
          }}
        >
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CourseModal = ({ open, course, teacherNames, onClose, onGeneratePDF, onEnroll, isEnrolled, hasPending, navigate, enrollmentBlocked = false }) => {
  const [imageError, setImageError] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  
  // Verificar si hay usuario logueado
  const usuario = JSON.parse(localStorage.getItem('usuarioConCedula') || '{}');
  const isUserLoggedIn = !!usuario?.id;
  
  if (!course) return null;

  const handleEnrollClick = async () => {
    if (!isUserLoggedIn || !onEnroll || enrollmentBlocked) return;
    
    setEnrolling(true);
    try {
      await onEnroll(course.id);
      onClose();
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={modalStyle}>
          {/* Header del Modal */}
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            bgcolor: 'background.paper', 
            borderBottom: '2px solid #4DABA4',
            p: 3,
            zIndex: 1,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e6f7f5 100%)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography 
                variant="h4" 
                className="text-green-aqua"
                sx={{ 
                  fontWeight: 700, 
                  flex: 1, 
                  pr: 2,
                  color: '#4DABA4'
                }}
                dangerouslySetInnerHTML={{ __html: course.title }}
              />
              <IconButton 
                onClick={onClose}
                className="border-green-aqua"
                sx={{ 
                  bgcolor: 'white',
                  border: '2px solid #4DABA4',
                  color: '#4DABA4',
                  '&:hover': { 
                    bgcolor: '#4DABA4',
                    color: 'white'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Contenido del Modal */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Box 
                  className="modal-image-container" 
                  sx={{ 
                    mb: 3, 
                    boxShadow: '0 10px 25px rgba(77, 171, 164, 0.2)',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '3px solid #4DABA4',
                    boxSizing: 'border-box',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 300,
                  }}
                >
                  <CardMedia
                    component="img"
                    image={imageError ? '/img/default.png' : (getCourseThumbnailSrc(course) || '/img/default.png')}
                    alt={course.title}
                    className="modal-course-image"
                    onError={() => setImageError(true)}
                    sx={{
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: 320,
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                </Box>
                
                {course.objective && (
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #e6f7f5 0%, #f0f9f8 100%)',
                    borderRadius: 3, 
                    p: 3,
                    border: '2px solid #4DABA4',
                    boxShadow: '0 4px 12px rgba(77, 171, 164, 0.15)'
                  }}>
                    <Typography 
                      variant="h6" 
                      className="text-green-aqua"
                      sx={{ 
                        mb: 2, 
                        fontWeight: 700, 
                        color: '#4DABA4',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      🎯 Objetivos del curso
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      className="ql-editor"
                      sx={{
                        lineHeight: 1.7,
                        color: '#2d3748',
                        p: '0 !important',
                        overflow: 'visible',
                        '& ul, & ol': { pl: 3, m: 0 },
                        '& p': { m: 0, mb: 1 },
                      }}
                      dangerouslySetInnerHTML={{ __html: course.objective }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={7}>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    className="text-green-aqua"
                    sx={{ 
                      fontWeight: 700, 
                      mb: 2,
                      color: '#4DABA4',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    📝 Descripción
                  </Typography>
                  <Typography 
                    variant="body1"
                    component="div"
                    className="ql-editor"
                    sx={{ 
                      lineHeight: 1.8, 
                      textAlign: 'justify',
                      color: '#2d3748',
                      bgcolor: 'white',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      overflow: 'visible',
                      '& ul, & ol': { pl: 3, m: 0 },
                      '& p': { m: 0, mb: 1 },
                    }}
                    dangerouslySetInnerHTML={{ __html: course.description || '' }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mb: 2,
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <CalendarIcon sx={{ color: '#4DABA4' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Fecha de creación
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748' }}>
                          {new Date(course.createdAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mb: 2,
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <PersonIcon sx={{ color: '#4DABA4' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Instructor
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748' }}>
                          {getTeacherDisplayName(course, teacherNames)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {course.guide && (
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e6f7f5 100%)',
                    borderRadius: 3, 
                    p: 3,
                    border: '2px solid #4DABA4',
                    boxShadow: '0 4px 12px rgba(77, 171, 164, 0.15)'
                  }}>
                    <Typography 
                      variant="h6" 
                      className="text-green-aqua"
                      sx={{ 
                        mb: 2, 
                        fontWeight: 700,
                        color: '#4DABA4',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      📖 Guía del curso
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        lineHeight: 1.7, 
                        color: '#2d3748',
                        bgcolor: 'white',
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      {course.guide}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Botón de inscripción - Solo si el usuario está logueado */}
          {isUserLoggedIn && (
            <Box sx={{ 
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              borderTop: '2px solid #4DABA4',
              p: 3,
              zIndex: 1,
              background: 'linear-gradient(135deg, #f8fafc 0%, #e6f7f5 100%)'
            }}>
              {isEnrolled ? (
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={() => {
                    onClose();
                    navigate(`/courses/Introcourse/${course.id}`, { state: { objeto: course.id } });
                  }}
                  startIcon={<PlayArrowIcon />}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.5,
                    backgroundColor: '#10B981',
                    color: 'white',
                    boxShadow: 2,
                    '&:hover': {
                      backgroundColor: '#059669',
                      boxShadow: 4
                    }
                  }}
                >
                  Continuar Curso
                </Button>
              ) : hasPending ? (
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  disabled
                  startIcon={<AccessTimeIcon />}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.5,
                    backgroundColor: '#F59E0B',
                    color: 'white',
                    boxShadow: 2,
                    '&.Mui-disabled': {
                      backgroundColor: '#F59E0B',
                      color: 'white'
                    }
                  }}
                >
                  Solicitud Pendiente
                </Button>
              ) : (
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleEnrollClick}
                  disabled={enrolling || enrollmentBlocked}
                  startIcon={enrolling ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SchoolIcon />}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.5,
                    backgroundColor: '#4DABA4',
                    color: 'white',
                    boxShadow: 2,
                    '&:hover': {
                      backgroundColor: '#3a8a84',
                      boxShadow: 4
                    },
                    '&.Mui-disabled': {
                      backgroundColor: enrollmentBlocked ? '#9ca3af' : '#4DABA4',
                      color: 'white'
                    }
                  }}
                >
                  {enrolling
                    ? 'Inscribiendo...'
                    : enrollmentBlocked
                      ? 'Completa el curso de bienvenida primero'
                      : 'Inscribirse al curso'}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

const LoadingSkeleton = () => (
  <Grid container spacing={4}>
    {[...Array(6)].map((_, index) => (
      <Grid item key={index}>
        <Card sx={{ width: 320, height: 480 }}>
          <Skeleton variant="rectangular" height={200} />
          <CardContent>
            <Skeleton variant="text" height={32} width="80%" />
            <Skeleton variant="text" height={20} />
            <Skeleton variant="text" height={20} />
            <Skeleton variant="text" height={20} width="60%" />
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" height={24} width={120} sx={{ borderRadius: 1 }} />
            </Box>
          </CardContent>
          <CardActions sx={{ gap: 1 }}>
            <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 1 }} />
          </CardActions>
        </Card>
      </Grid>
    ))}
  </Grid>
);

export default function Cview({ compact = false, externalSearchTerm = null, onSearchChange = null, onLoginRequired = null, onEnrollSuccess = null }) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [successModal, setSuccessModal] = useState({ open: false, courseTitle: '' });
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  
  // Usar searchTerm externo si está disponible, sino usar el interno
  const searchTerm = externalSearchTerm !== null ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = onSearchChange || setInternalSearchTerm;
  const [allCourses, setAllCourses] = useState([]);
  const [userCourses, setUserCourses] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({});
  const [loading, setLoading] = useState(true);
  const [inscriptionAlert, setInscriptionAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [teacherNames, setTeacherNames] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalCourses: 0
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [sharedCourseInfo, setSharedCourseInfo] = useState(null);
  const [sharedCourseLoading, setSharedCourseLoading] = useState(false);
  const sharedCourseFetchRef = useRef(false);

  const { usuarioConCedula: usuarioAuth, isAdminOrTeacher } = useUsuarioWithRole();
  const {
    isBlocked: welcomeBlocked,
    courseId: welcomeCourseId,
    courseTitle: welcomeCourseTitle,
    isWelcomeCourse,
  } = useWelcomeCourseStatus();

  const usuario = useMemo(() => {
    if (usuarioAuth?.id) return usuarioAuth;
    try {
      const raw = localStorage.getItem('usuarioConCedula');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [usuarioAuth]);

  // Función para verificar si el usuario está inscrito en un curso
  const isUserEnrolled = (courseId) => {
    return userCourses.some(userCourse => userCourse.id === courseId);
  };

  // Función para verificar si el usuario tiene una solicitud pendiente
  const hasPendingRequest = (courseId) => {
    return pendingRequests[courseId] !== undefined;
  };
  
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const token = tokenUtils.get();
        const sessionOk =
          Boolean(usuario?.id) && Boolean(token && !tokenUtils.isExpired(token));

        let loadedCourses = [];

        // Catálogo público (IsPublic) siempre disponible; con sesión se mezclan recomendaciones
        const publicCourses = (await coursesService.getPublicCourses()) || [];

        if (sessionOk) {
          let suggested = [];
          try {
            const response = await coursesService.getSuggestedCourses(usuario.id, navigate, 1, 10);
            suggested = coursesService.normalizeCoursesList(response.courses || response);
          } catch {
            suggested = [];
          }

          const byId = new Map();
          [...publicCourses, ...suggested].forEach((course) => {
            if (course?.id != null) byId.set(String(course.id), course);
          });
          loadedCourses = Array.from(byId.values());
          setAllCourses(loadedCourses);
          setPagination({
            currentPage: 1,
            pageSize: loadedCourses.length,
            totalPages: 1,
            totalCourses: loadedCourses.length,
          });
        } else {
          loadedCourses = publicCourses;
          setAllCourses(loadedCourses);
          setPagination({
            currentPage: 1,
            pageSize: loadedCourses?.length || 0,
            totalPages: 1,
            totalCourses: loadedCourses?.length || 0
          });
        }

        // Obtener los cursos del usuario (solo con sesión válida)
        if (sessionOk) {
          try {
            const userCoursesData = await coursesService.getUserCourses(usuario.id, navigate);
            setUserCourses(userCoursesData);
          } catch (error) {
            console.error('Error al obtener cursos del usuario:', error);
            setUserCourses([]);
          }

          // Solo docentes/admin pueden consultar pending-requests; los estudiantes reciben 403.
          if (isAdminOrTeacher) {
            const pendingRequestsMap = {};
            await Promise.all(
              loadedCourses.map(async (course) => {
                try {
                  const pendingRequestsData = await coursesService.getCoursePendingRequests(course.id, navigate);
                  const userPendingRequest = pendingRequestsData.find(
                    (request) => request.userId === usuario.id
                  );
                  if (userPendingRequest) {
                    pendingRequestsMap[course.id] = userPendingRequest;
                  }
                } catch (error) {
                  if (error.response?.status !== 403) {
                    console.error(`Error al obtener solicitudes pendientes para curso ${course.id}:`, error);
                  }
                }
              })
            );
            setPendingRequests(pendingRequestsMap);
          } else {
            setPendingRequests({});
          }
        } else {
          // Si no hay usuario, limpiar los estados relacionados
          setUserCourses([]);
          setPendingRequests({});
        }

        const uniqueTeacherIds = [
          ...new Set(
            loadedCourses
              .map((c) => getCourseTeacherId(c))
              .filter((id) => id != null && Number(id) > 0)
          ),
        ];
        const namesMap = {};

        // GET /Teacher/:id exige Bearer; sin sesión solo usamos nombre embebido en el curso (getTeacherDisplayName)
        if (sessionOk && uniqueTeacherIds.length > 0) {
          await Promise.all(uniqueTeacherIds.map(async (id) => {
            try {
              const response = await teacherService.getTeacher(id);
              namesMap[id] = response.name || response.nombre || response.Name || 'Instructor no disponible';
            } catch (error) {
              if (error.response?.status !== 404) {
                console.error(`Error al obtener instructor con ID ${id}:`, error);
              }
              namesMap[id] = 'Instructor no disponible';
            }
          }));
        }

        setTeacherNames(namesMap);
      } catch (err) {
        console.error('Error al obtener los cursos:', err);
        setInscriptionAlert({
          open: true,
          message: 'Error al cargar los cursos',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [usuario?.id, isAdminOrTeacher, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sharedCourseId = searchParams.get('shared');
    const sharedCourseTitle = searchParams.get('title');
    const notificationCourseId = searchParams.get('courseId');

    sharedCourseFetchRef.current = false;

    if (sharedCourseId) {
      const decodedTitle = safeDecodeURIComponent(sharedCourseTitle) || 'Curso compartido';
      setSharedCourseInfo({
        id: sharedCourseId,
        title: decodedTitle,
      });
      setInscriptionAlert({
        open: true,
        message: `¡Curso compartido: ${decodedTitle}`,
        severity: 'info',
      });
    } else if (notificationCourseId) {
      // Handle courseId from notification
      const targetCourse = allCourses.find((course) => matchesCourseId(course, notificationCourseId));
      if (targetCourse) {
        setSharedCourseInfo({
          id: notificationCourseId,
          title: stripHtmlTags(targetCourse.title) || 'Curso seleccionado'
        });
        setInscriptionAlert({
          open: true,
          message: `Curso de la notificación: ${stripHtmlTags(targetCourse.title)}`,
          severity: 'info',
        });
      } else {
        // Course not found in loaded courses, set info anyway for filtering
        setSharedCourseInfo({
          id: notificationCourseId,
          title: 'Curso seleccionado'
        });
        setInscriptionAlert({
          open: true,
          message: 'Mostrando curso de la notificación',
          severity: 'info',
        });
      }
    } else {
      setSharedCourseInfo(null);
    }
  }, [location.search, allCourses]);

  useEffect(() => {
    sharedCourseFetchRef.current = false;
  }, [sharedCourseInfo?.id, usuario?.id]);

  useEffect(() => {
    if (!sharedCourseInfo?.id || loading) return;

    const exists = allCourses.some((course) => matchesCourseId(course, sharedCourseInfo.id));
    const titleMatch = findPublicCourseBySharedInfo(allCourses, sharedCourseInfo);
    if (exists || titleMatch) return;
    if (sharedCourseFetchRef.current) return;

    sharedCourseFetchRef.current = true;
    setSharedCourseLoading(true);

    (async () => {
      try {
        const course = await coursesService.resolveSharedCourse(sharedCourseInfo.id, navigate);
        if (course?.id != null) {
          setAllCourses((prev) => {
            if (prev.some((item) => matchesCourseId(item, course.id))) return prev;
            return [...prev, course];
          });
        }
      } catch (error) {
        console.error('Error al cargar curso compartido:', error);
      } finally {
        setSharedCourseLoading(false);
      }
    })();
  }, [sharedCourseInfo, allCourses, loading, usuario?.id, navigate]);

  const filteredCourses = useMemo(() => {
    const sourceCourses = allCourses || [];

    if (sharedCourseInfo?.id) {
      let sharedMatches = sourceCourses.filter((course) =>
        matchesCourseId(course, sharedCourseInfo.id)
      );

      if (sharedMatches.length === 0) {
        const byTitle = findPublicCourseBySharedInfo(sourceCourses, sharedCourseInfo);
        if (byTitle) {
          sharedMatches = [
            coursesService.normalizeCourse(byTitle, sharedCourseInfo.id),
          ];
        }
      }

      if (sharedMatches.length === 0 && !sharedCourseLoading) {
        sharedMatches = [buildSharedCoursePreview(sharedCourseInfo)];
      }

      return sharedMatches;
    }

    let filtered = sourceCourses.filter((course) => course && course.id != null);

    // Gate: solo el curso de bienvenida hasta completarlo
    if (welcomeBlocked && welcomeCourseId) {
      filtered = filtered.filter((course) => String(course.id) === String(welcomeCourseId));
    }

    filtered = filtered.filter((course) => {
      const title = (course.title || '').toLowerCase();
      const description = (course.description || '').toLowerCase();
      const teacherName = getTeacherDisplayName(course, teacherNames).toLowerCase();
      const term = searchTerm.toLowerCase();
      return title.includes(term) || description.includes(term) || teacherName.includes(term);
    });

    if (usuario?.id) {
      filtered = filtered.filter((course) => {
        // Si está bloqueado por bienvenida, mostrar el curso aunque ya esté inscrito
        if (welcomeBlocked && String(course.id) === String(welcomeCourseId)) {
          return true;
        }
        const isEnrolled = isUserEnrolled(course.id);
        const hasPending = hasPendingRequest(course.id);

        return !isEnrolled || hasPending;
      });
    }

    // Si el gate está activo y el welcome no vino en el catálogo, mostrar una tarjeta mínima
    if (
      welcomeBlocked &&
      welcomeCourseId &&
      !filtered.some((c) => String(c.id) === String(welcomeCourseId))
    ) {
      filtered = [
        {
          id: welcomeCourseId,
          title: welcomeCourseTitle || 'Curso de bienvenida',
          description: 'Debes completar este curso antes de acceder al resto del catálogo.',
          isWelcome: true,
          createdAt: new Date().toISOString(),
        },
      ];
    }

    return filtered;
  }, [allCourses, searchTerm, teacherNames, sharedCourseInfo, sharedCourseLoading, userCourses, pendingRequests, usuario, welcomeBlocked, welcomeCourseId, welcomeCourseTitle]);

  const handleOpenModal = (course) => {
    setSelectedCourse(course);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCourse(null);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal({ open: false, courseTitle: '' });
  };

  const handleCloseInscriptionAlert = () => {
    setInscriptionAlert({ ...inscriptionAlert, open: false });
  };

  const toggleDescription = (courseId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const handleEnroll = async (courseId) => {
    try {
      if (welcomeBlocked && !isWelcomeCourse(courseId)) {
        setInscriptionAlert({
          open: true,
          message: 'Debes completar el curso de bienvenida antes de inscribirte a otros cursos.',
          severity: 'warning',
        });
        return;
      }

      await coursesService.assignDirectly(usuario.id, courseId, navigate);

      analyticsService.trackEvent('course_enroll', {
        userId: usuario.id,
        properties: { courseId },
      });
      
      const course = allCourses.find(c => c.id === courseId);
      const courseTitle = course ? stripHtmlTags(course.title) : 'el curso';
      
      const updatedUserCourses = await coursesService.getUserCourses(usuario.id);
      setUserCourses(updatedUserCourses);

      if (onEnrollSuccess) {
        onEnrollSuccess(courseId, courseTitle);
        return;
      }
      
      if (openModal && selectedCourse?.id === courseId) {
        setSuccessModal({ open: true, courseTitle });
        setOpenModal(false);
        setSelectedCourse(null);
        return;
      }

      setInscriptionAlert({
        open: true,
        message: `Te has inscrito correctamente al curso "${courseTitle}".`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error al inscribirse:', error);
      const apiMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Error al inscribirse al curso. Inténtalo de nuevo.';
      setInscriptionAlert({
        open: true,
        message: apiMessage,
        severity: 'error',
      });
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text('Material del Curso', 20, 20);
    doc.save('Material_Curso.pdf');
  };

  const clearSharedFilter = () => {
    setSearchTerm('');
    setSharedCourseInfo(null);
    const params = new URLSearchParams(location.search);
    params.delete('shared');
    params.delete('title');
    params.delete('courseId');
    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  };

  const clearSearch = () => {
    clearSharedFilter();
  };

  const loadMoreCourses = async () => {
    if (loadingMore || pagination.currentPage >= pagination.totalPages) return;
    
    try {
      setLoadingMore(true);
      const nextPage = pagination.currentPage + 1;
      const response = await coursesService.getSuggestedCourses(usuario.id, navigate, nextPage, pagination.pageSize);
      const nextCourses = coursesService.normalizeCoursesList(response.courses || []);

      // Agregar nuevos cursos a la lista existente
      setAllCourses(prev => [...prev, ...nextCourses]);
      
      // Actualizar paginación
      setPagination(prev => ({
        ...prev,
        currentPage: response.currentPage || nextPage,
        totalPages: response.totalPages || prev.totalPages,
        totalCourses: response.totalCourses || prev.totalCourses
      }));
    } catch (error) {
      console.error('Error al cargar más cursos:', error);
      setInscriptionAlert({
        open: true,
        message: 'Error al cargar más cursos',
        severity: 'error',
      });
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Box sx={{ bgcolor: compact ? 'transparent' : '#f8fafc', minHeight: compact ? 'auto' : '100vh', py: compact ? 0 : 6 }}>
      <Container maxWidth="xl">
        {welcomeBlocked && (
          <WelcomeCourseBanner
            courseId={welcomeCourseId}
            courseTitle={welcomeCourseTitle}
            sx={{ mt: compact ? 0 : 0, mb: 3 }}
          />
        )}
        {/* Header y Búsqueda - Solo mostrar si no es compacto */}
        {!compact && (
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, mb: 2 }}
              className="text-green-aqua"
            >
              {sharedCourseInfo ? (location.search.includes('courseId') ? 'Curso de Notificación' : 'Curso Compartido') : 'Catálogo de Cursos'}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              {sharedCourseInfo 
                ? (location.search.includes('courseId') 
                    ? `Curso de notificación: ${sharedCourseInfo.title}`
                    : `Curso compartido: ${sharedCourseInfo.title}`)
                : 'Descubre y matriculate a nuestros cursos especializados'
              }
            </Typography>
            
            {sharedCourseInfo && (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={clearSharedFilter}
                  startIcon={<CloseIcon />}
                  className="border-green-aqua"
                  sx={{ 
                    mb: 2,
                    color: '#4DABA4',
                    borderColor: '#4DABA4',
                    '&:hover': {
                      borderColor: '#4DABA4',
                      backgroundColor: 'rgba(77, 171, 164, 0.1)'
                    }
                  }}
                >
                  Ver todos los cursos
                </Button>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <TextField
                label={sharedCourseInfo 
                  ? (location.search.includes('courseId') 
                      ? "Buscar en este curso de notificación..." 
                      : "Buscar en este curso...")
                  : "Buscar cursos, instructores o temas..."
                }
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!!sharedCourseInfo}
                sx={{ 
                  maxWidth: 600,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    boxShadow: 1,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className='text-green-aqua' />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={clearSearch} size="small">
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            {searchTerm && (
              <Typography variant="body2" color="text.secondary">
                {filteredCourses.length} curso{filteredCourses.length !== 1 ? 's' : ''} encontrado{filteredCourses.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        )}

        {compact && sharedCourseInfo && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              borderRadius: 2,
              bgcolor: 'rgba(77, 171, 164, 0.08)',
              border: '1px solid rgba(77, 171, 164, 0.25)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3d8f89' }}>
              {location.search.includes('courseId')
                ? `Curso de notificación: ${sharedCourseInfo.title}`
                : `Curso compartido: ${sharedCourseInfo.title}`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={clearSharedFilter}
              startIcon={<CloseIcon />}
              sx={{
                textTransform: 'none',
                color: '#4DABA4',
                borderColor: '#4DABA4',
                '&:hover': {
                  borderColor: '#4DABA4',
                  backgroundColor: 'rgba(77, 171, 164, 0.1)',
                },
              }}
            >
              Ver todos los cursos
            </Button>
          </Box>
        )}

        {/* Grid de Cursos */}
        {loading || (sharedCourseInfo?.id && sharedCourseLoading) ? (
          <LoadingSkeleton />
        ) : filteredCourses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SchoolIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {sharedCourseInfo
                ? 'No se encontró el curso compartido'
                : searchTerm
                  ? 'No se encontraron cursos'
                  : 'No hay cursos disponibles'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: sharedCourseInfo ? 2 : 0 }}>
              {sharedCourseInfo
                ? 'Es posible que necesites iniciar sesión o que el curso ya no esté disponible.'
                : searchTerm
                  ? 'Intenta con otros términos de búsqueda'
                  : 'Vuelve pronto para ver nuevos cursos'}
            </Typography>
            {sharedCourseInfo && (
              <Button
                variant="outlined"
                onClick={clearSharedFilter}
                startIcon={<CloseIcon />}
                sx={{
                  textTransform: 'none',
                  color: '#4DABA4',
                  borderColor: '#4DABA4',
                }}
              >
                Ver todos los cursos
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Grid 
              container 
              spacing={compact ? 2.5 : 4} 
              justifyContent={compact ? "flex-start" : "center"}
              alignItems="stretch"
              sx={{ 
                mb: compact ? 0 : 4,
              }}
            >
              {filteredCourses.map((course) => (
                <Grid 
                  item 
                  key={course.id} 
                  xs={12} 
                  sm={6} 
                  md={compact ? 3 : 6} 
                  lg={compact ? 3 : 4}
                  sx={compact ? {
                    display: 'flex',
                    width: '280px !important',
                    maxWidth: '280px !important',
                    flexBasis: '280px !important',
                    '@media (max-width: 1280px)': {
                      width: '280px !important',
                      maxWidth: '280px !important',
                      flexBasis: '280px !important',
                    },
                    '@media (max-width: 960px)': {
                      width: 'calc(50% - 10px) !important',
                      maxWidth: 'calc(50% - 10px) !important',
                      flexBasis: 'calc(50% - 10px) !important',
                    },
                    '@media (max-width: 600px)': {
                      width: '100% !important',
                      maxWidth: '100% !important',
                      flexBasis: '100% !important',
                    }
                  } : {}}
                >
                  <CourseCard
                    course={course}
                    teacherNames={teacherNames}
                    onOpenModal={handleOpenModal}
                    onToggleDescription={toggleDescription}
                    isExpanded={expandedDescriptions[course.id]}
                    onEnroll={handleEnroll}
                    isEnrolled={isUserEnrolled(course.id)}
                    hasPending={hasPendingRequest(course.id)}
                    navigate={navigate}
                    compact={compact}
                    onLoginRequired={onLoginRequired}
                    enrollmentBlocked={welcomeBlocked && !isWelcomeCourse(course.id)}
                  />
                </Grid>
              ))}
            </Grid>
            
            {/* Botón para cargar más cursos */}
            {usuario?.id && pagination.currentPage < pagination.totalPages && !sharedCourseInfo && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreCourses}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={20} /> : <SchoolIcon />}
                  className="border-green-aqua"
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#4DABA4',
                    borderColor: '#4DABA4',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#4DABA4',
                      backgroundColor: 'rgba(77, 171, 164, 0.1)'
                    },
                    '&.Mui-disabled': {
                      borderColor: '#4DABA4',
                      color: '#4DABA4'
                    }
                  }}
                >
                  {loadingMore ? 'Cargando...' : 'Cargar más cursos'}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Página {pagination.currentPage} de {pagination.totalPages} • {pagination.totalCourses} cursos en total
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Alertas */}
        <Snackbar
          open={inscriptionAlert.open}
          autoHideDuration={5000}
          onClose={handleCloseInscriptionAlert}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseInscriptionAlert}
            severity={inscriptionAlert.severity}
            sx={{ 
              width: '100%',
              borderRadius: 2,
              boxShadow: 3
            }}
          >
            {inscriptionAlert.message}
          </Alert>
        </Snackbar>

        {/* Modal */}
        <CourseModal
          open={openModal}
          course={selectedCourse}
          teacherNames={teacherNames}
          onClose={handleCloseModal}
          onGeneratePDF={generatePDF}
          onEnroll={handleEnroll}
          isEnrolled={selectedCourse ? isUserEnrolled(selectedCourse.id) : false}
          hasPending={selectedCourse ? hasPendingRequest(selectedCourse.id) : false}
          navigate={navigate}
          enrollmentBlocked={
            welcomeBlocked && selectedCourse ? !isWelcomeCourse(selectedCourse.id) : false
          }
        />

        {/* Modal de éxito */}
        <SuccessModal
          open={successModal.open}
          courseTitle={successModal.courseTitle}
          onClose={handleCloseSuccessModal}
        />
      </Container>
    </Box>
  );
}

export { CourseCard };